import { Injectable } from '@angular/core';
import {
  ApplicantAppEntityApplicantRead,
  ApplicantService,
  ApplicationAppEntityApplicationRead,
  ApplicationAttachmentService,
  ApplicationService,
  AttachmentAttachmentRead,
  PatchApplicantApplicantItemRequestParams,
  RoadOccupationService,
  RoadOccupationStageAppEntityRoadOccupationStageWrite,
  RoadOccupationStageService,
  PatchContractorContractorItemRequestParams,
  ContractorService,
  ContractorAppEntityContractorRead,
  ApplicationAppEntityApplicationWrite,
  DrainageSurfaceCharacteristicService,
  DrainageSurfaceCharacteristicAppEntityDrainageSurfaceCharacteristicWrite,
  RoadOccupationAppEntityRoadOccupationRead,
  DrainageSurfaceCharacteristicAppEntityApplicationRead,
  ApplicationAttachmentAppEntityApplicationRead,
  ServiceAttachmentPositionService, ServiceAttachmentPositionBaseReadServiceAttachmentPositionItemRead
} from '../openapi';
import { concatMap, forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { dateToPLString, toNumber } from './helpers';
import { ApplicationListItem, ApplicationPayment, AttachmentPositions } from './application.common.model';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import {
  RoadOccupationStageFormValue
} from '../containers/applications/application/common/occupation-data-section/forms/road-occupation-stage.form';
import {
  RoadOccupationFormValue
} from '../containers/applications/application/common/occupation-data-section/forms/road-occupation.form';

export type ApplicationCode = 'roboczy' | 'zalatwiony' | 'wyslany' | 'zatwierdzony';



export function stageFormValueToWriteModel(stage: RoadOccupationStageFormValue): RoadOccupationStageAppEntityRoadOccupationStageWrite {
  return {
    startDate: stage?.startDate || undefined,
    endDate: stage?.endDate || undefined,
    geometry: stage?.geometry || undefined,
    roadWidth: toNumber(stage?.roadWidth) || undefined,
    roadWidth2: toNumber(stage?.roadWidth2) || undefined,
    roadWidth3: toNumber(stage?.roadWidth3) || undefined,
    roadLength: toNumber(stage?.roadLength) || undefined,
    roadLength2: toNumber(stage?.roadLength2) || undefined,
    roadLength3: toNumber(stage?.roadLength3) || undefined,
    roadArea: toNumber(stage?.roadArea) || undefined,
    roadArea2: toNumber(stage?.roadArea2) || undefined,
    roadArea3: toNumber(stage?.roadArea3) || undefined,
    sidewalkArea: toNumber(stage?.sidewalkArea) || undefined,
    sidewalkWidth: toNumber(stage?.sidewalkWidth) || undefined,
    sidewalkLength: toNumber(stage?.sidewalkLength) || undefined,
    bridgeStructureWidth: toNumber(stage?.bridgeStructureWidth) || undefined,
    bridgeStructureLength: toNumber(stage?.bridgeStructureLength) || undefined,
    bridgeStructureArea: toNumber(stage?.bridgeStructureArea) || undefined,
    otherElementsArea: toNumber(stage?.otherElementsArea) || undefined,
    otherElementsWidth: toNumber(stage?.otherElementsWidth) || undefined,
    otherElementsLength: toNumber(stage?.otherElementsLength) || undefined
  };
}

@Injectable({
  providedIn: 'root'
})
export class ApplicationCommonService {
  constructor(
    private applicationService: ApplicationService,
    private applicantService: ApplicantService,
    private roadOccupationService: RoadOccupationService,
    private roadOccupationStageService: RoadOccupationStageService,
    private applicationAttachmentService: ApplicationAttachmentService,
    private contractorService: ContractorService,
    private drainageSurfaceCharacteristicService: DrainageSurfaceCharacteristicService,
    private http: HttpClient,
    private serviceAttachmentPositionService: ServiceAttachmentPositionService
  ) {
  }

  public loadAttachmentPositionsByApplicationId(applicationId: string): Observable<AttachmentPositions> {
    return this.http.get<ServiceAttachmentPositionBaseReadServiceAttachmentPositionItemRead[]>(`${environment.apiBasePath}/service-attachments-positions/by-application-id/${applicationId}?pagination=false`)
      .pipe(
        map(items => items.map(item => {
          return {
            tag: item?.tag || '',
            description: item?.name || '',
            required: !!item?.isRequired,
            multiple: !!item?.isMultiple,
            payment: item?.fee
          };
        }) || [])
      );
  }

  public updateAssureReceptionWater(application: ApplicationAppEntityApplicationRead | null, payload: ApplicationAppEntityApplicationWrite, drainageSurfaceCharacteristics: DrainageSurfaceCharacteristicAppEntityDrainageSurfaceCharacteristicWrite[]): Observable<ApplicationAppEntityApplicationRead> {
    const deleteRequests = (application?.drainageSurfaceCharacteristics || []).map((v: DrainageSurfaceCharacteristicAppEntityApplicationRead) => this.drainageSurfaceCharacteristicService.deleteDrainageSurfaceCharacteristicDrainageSurfaceCharacteristicItem({
        id: v.id || ''
      })
    );
    return (deleteRequests.length ? forkJoin(deleteRequests) : of([])).pipe(
      switchMap(() =>
        forkJoin(
          drainageSurfaceCharacteristics.map(v => this.drainageSurfaceCharacteristicService.postDrainageSurfaceCharacteristicDrainageSurfaceCharacteristicCollection({
              drainageSurfaceCharacteristic: {
                ...v,
                application: application?.uuid
              }
            })
          )
        )
      ),
      concatMap(() => this.applicationService.patchApplicationApplicationItem({
        id: application?.uuid || '',
        application: {
          ...payload
        }
      }))
    );
  }

  public createApplication(serviceTypeId: string, applicants: PatchApplicantApplicantItemRequestParams[], application?: ApplicationAppEntityApplicationWrite): Observable<ApplicationAppEntityApplicationRead> {
    return forkJoin(applicants.map(v => v.applicant).map(applicant => this.applicantService.postApplicantApplicantCollection({ applicant }))).pipe(
      switchMap(applicants =>
        this.applicationService.postApplicationApplicationCollection({
          application: {
            ...application,
            service: serviceTypeId,
            applicants: applicants.map(applicant => applicant?.uuid || '')
          }
        })
      )
    );
  }

  public updateApplicants(applicationId: string, applicants: PatchApplicantApplicantItemRequestParams[], application?: ApplicationAppEntityApplicationWrite): Observable<ApplicationAppEntityApplicationRead> {
    // for existing
    const applicantPatchRequests: Observable<ApplicantAppEntityApplicantRead>[] = applicants.filter(v => !!v.id).map(item => this.applicantService.patchApplicantApplicantItem(item));

    // for new when id is empty string
    const applicantPostRequests: Observable<ApplicantAppEntityApplicantRead>[] = applicants.filter(v => !v.id).map(v => v.applicant).map(applicant => this.applicantService.postApplicantApplicantCollection({
      applicant
    }));
    return forkJoin([
      ...applicantPostRequests,
      ...applicantPatchRequests
    ]).pipe(
      switchMap((applicants) => this.applicationService.patchApplicationApplicationItem({
        id: applicationId,
        application: { ...application, applicants: applicants.map(applicant => applicant?.uuid || '') }
      }))
    );
  }

  public updateContractors(applicationId: string, contractors: PatchContractorContractorItemRequestParams[]): Observable<ApplicationAppEntityApplicationRead> {
    // for existing
    const contractorPatchRequests: Observable<ContractorAppEntityContractorRead>[] = contractors.filter(v => !!v.id)
      .map(item => this.contractorService.patchContractorContractorItem(item));

    // for new when id is empty string
    const contractorPostRequests: Observable<ContractorAppEntityContractorRead>[] = contractors.filter(v => !v.id)
      .map(v => v.contractor).map(contractor => this.contractorService.postContractorContractorCollection({
        contractor
      }));

    return forkJoin([
      ...contractorPatchRequests,
      ...contractorPostRequests
    ]).pipe(
      switchMap((contractors) => this.applicationService.patchApplicationApplicationItem({
        id: applicationId, application: { contractors: contractors.map(contractor => contractor?.uuid || '') }
      }))
    );
  }

  public updateOccupations(application: ApplicationAppEntityApplicationRead | null, occupations: RoadOccupationFormValue[]): Observable<ApplicationAppEntityApplicationRead> {
    const deleteRequests = (application?.roadOccupations || []).map((v: any) => this.roadOccupationService.deleteRoadOccupationRoadOccupationItem({ id: v?.uuid || '' }));
    return (deleteRequests.length ? forkJoin(deleteRequests) : of([])).pipe(
      switchMap(() => {
        return forkJoin(occupations.map(v => {
          return this.roadOccupationService.postRoadOccupationRoadOccupationCollection({
            roadOccupation: {
              application: application?.uuid,
              locationDecisionNumber: v?.locationDecisionNumber,
              locationDecisionDate: v?.locationDecisionDate || undefined,
              agreementNumber: v?.agreementNumber || undefined,
              agreementDate: v?.agreementDate || undefined,
              purpose: v?.purpose,
              city: v?.city,
              occupationType: v?.occupationType,
              typeOfFailure: v?.typeOfFailure,
              surfaceViolation: v?.surfaceViolation,
              workPurpose: v?.workPurpose
            }
          }).pipe(
            switchMap((roadOccupation: RoadOccupationAppEntityRoadOccupationRead) => forkJoin(
                v.stages.map(stage => this.roadOccupationStageService.postRoadOccupationStageRoadOccupationStageCollection({
                    roadOccupationStage: { ...stageFormValueToWriteModel(stage), roadOccupation: roadOccupation?.uuid }
                  })
                )
              )
            )
          );
        }));
      }),
      switchMap(() => this.applicationService.getApplicationItemApplicationItem({ id: application?.uuid || '' }))
    );
  }

  public updateApplicationAttachments(
    application: ApplicationAppEntityApplicationRead, attachments: AttachmentAttachmentRead[]
  ): Observable<any> {
    if (!attachments.length) {
      return this.applicationService.getApplicationItemApplicationItem({ id: application?.uuid || '' });
    }
    const oldIds: string[] = application?.attachments?.map((v: ApplicationAttachmentAppEntityApplicationRead) => v.id || '') || [];
    if (oldIds?.length) {
      return forkJoin(oldIds.map(v => this.applicationAttachmentService.deleteApplicationAttachmentItemApplicationAttachmentItem({
        id: v
      }))).pipe(
        switchMap(() => forkJoin(attachments?.map(attachment => this.applicationAttachmentService.postApplicationAttachmentCollectionApplicationAttachmentCollection({
          applicationAttachment: {
            application: application?.uuid,
            attachment: attachment?.id
          }
        }))).pipe(
          switchMap(() => this.applicationService.getApplicationItemApplicationItem({ id: application?.uuid || '' }))
        ))
      );
    } else {
      return forkJoin(
        attachments?.map(attachment =>
          this.applicationAttachmentService.postApplicationAttachmentCollectionApplicationAttachmentCollection({
            applicationAttachment: {
              application: application?.uuid,
              attachment: attachment?.id
            }
          })
        )
      ).pipe(
        switchMap(() => this.applicationService.getApplicationItemApplicationItem({ id: application?.uuid || '' }))
      );
    }
  }

  public loadApplicationList(code: ApplicationCode): Observable<ApplicationListItem[]> {
    return this.applicationService.getApplicationCollectionApplicationCollection({
      pagination: false,
      statusCode: code,
      orderUpdatedAt: code === 'roboczy' ? 'desc' : undefined,
      orderApprovedAt: code === 'zatwierdzony' ? 'desc' : undefined,
      orderSentAt: code === 'wyslany' || code === 'zalatwiony' ? 'desc' : undefined
    }).pipe(
      map((applications: Array<ApplicationAppEntityApplicationRead>) => applications.filter(v => !!v?.service).map(v => ({
          tag: `${v?.service?.name || ''} ${dateToPLString(v?.createdAt) || ''} ${dateToPLString(v?.updatedAt) || ''} ${dateToPLString(v?.sentAt) || ''} ${dateToPLString(v?.approvedAt) || ''}`,
          application: v
        }))
      )
    );
  }

  public loadApplicationPayments(applicationId: string): Observable<ApplicationPayment> {
    return this.http.post<ApplicationPayment>(`${environment.apiBasePath}/application/${applicationId}/cost-forecast`, {});
  }


  public signApplication(applicationId: string): Observable<{ result: string; redirect_url: string }> {
    return this.http.get<{
      result: string;
      redirect_url: string
    }>(`${environment.apiBasePath}/application/${applicationId}/sign-attachments`);
  }

}
